import { Ref, ref } from 'vue';
import { IData, IList, IResponse } from '@/types/interfaces';
import isScrollEnd, { DIRECTION } from '@/helpers/isScrollEnd';

export interface IUsePagination {
  scroller: Ref<null | HTMLElement>;
  pageIdx: Ref<number>;
  isPageLoading: Ref<boolean>;
  onScroll: (callback: () => Promise<IResponse<IData<IList>>>, direction: DIRECTION, isPrevent?: boolean) => void;
  onCacheScroll: (callback: () => Promise<any[]>, direction: DIRECTION, lastPageIdx: number, currentPageIdx: number, isPrevent?: boolean, lastSlide?: boolean) => void;
  setLastPage: (items: number, itemsOnPage: number) => void
}

export default function UsePagination(END_PIXEL_CORRECTION = 5): IUsePagination {
  const scroller = ref<null | HTMLElement>(null);
  const lastPage = ref(-1);
  const pageIdx = ref(0);
  const isPageLoading = ref(false);

  function checkCorrectUsage() {
    if (lastPage.value === -1) {
      console.error('[UsePagination]: You have to init "lastPage" value using "setLastPage" function');
      return false;
    }

    if (!scroller.value) {
      console.error('[UsePagination]: You have to init "scroller" ref on HTMLElement');
      return false;
    }

    return true;
  }

  function setLastPage(items: number, itemsOnPage: number) {
    lastPage.value = Math.floor(items / itemsOnPage) + 1;
  }

  function onCacheScroll(callback: () => Promise<any[]>, direction: DIRECTION, lastPageIdx: number, currentPageIdx: number, isPrevent = false, lastSlide = false): void {
    if (isPrevent || isPageLoading.value) {
      return;
    }

    const isEnd = lastSlide || isScrollEnd(scroller.value!, direction, END_PIXEL_CORRECTION);

    if (isEnd && lastPageIdx !== currentPageIdx) {
      isPageLoading.value = true;
      callback().finally(() => { isPageLoading.value = false; });
    }
  }

  function onScroll(callback: () => Promise<IResponse<IData<IList>>>, direction: DIRECTION, isPrevent = false): void {
    if (!checkCorrectUsage()) {
      return;
    }

    if (isPrevent || isPageLoading.value) {
      return;
    }

    const isEnd = isScrollEnd(scroller.value!, direction, END_PIXEL_CORRECTION);

    if (isEnd && lastPage.value !== pageIdx.value + 1) {
      isPageLoading.value = true;
      callback()
        .then((response) => {
          if (response.success) {
            pageIdx.value += 1;
            setLastPage(response.results.data.paging.items, response.results.data.paging.itemsOnPage);
          }
        })
        .finally(() => { isPageLoading.value = false; });
    }
  }

  return {
    scroller,
    pageIdx,
    isPageLoading,
    onScroll,
    setLastPage,
    onCacheScroll,
  };
}
